import { invert4, transformDirection3, transformPoint3, type Mat4 } from './matrix';
import { add3, length3, normalize3, scale3, sub3, type Vec3 } from './vector';

export interface Ray {
  readonly origin: Vec3;
  readonly direction: Vec3;
}

/** Unprojects CSS-pixel viewport coordinates. The ray starts on the camera's near plane. */
export function rayFromScreen(x: number, y: number, width: number, height: number, inverseViewProjection: Mat4): Ray {
  if (!(width > 0 && height > 0)) throw new RangeError('Ray viewport must have positive width and height');
  const ndcX = 2 * x / width - 1;
  const ndcY = 1 - 2 * y / height;
  const origin = transformPoint3([ndcX, ndcY, 0], inverseViewProjection);
  // A finite intermediate depth also supports perspective projections with an infinite far plane.
  const distant = transformPoint3([ndcX, ndcY, 0.5], inverseViewProjection);
  return { origin, direction: normalize3(sub3(distant, origin)) };
}

export function pointOnRay(ray: Ray, distance: number): Vec3 {
  return add3(ray.origin, scale3(normalize3(ray.direction), distance));
}

/** Intersects a transformed unit box (-0.5..0.5 on each axis), returning world distance or null. */
export function intersectRayOBB(ray: Ray, worldMatrix: Mat4): number | null {
  const directionLength = length3(ray.direction);
  if (!(directionLength > 0)) return null;
  let inverse: Mat4;
  try {
    inverse = invert4(worldMatrix);
  } catch (error) {
    if (error instanceof RangeError) return null;
    throw error;
  }
  const origin = transformPoint3(ray.origin, inverse);
  const direction = transformDirection3(ray.direction, inverse);
  let entry = -Infinity;
  let exit = Infinity;
  for (let axis = 0; axis < 3; axis++) {
    const o = origin[axis]!;
    const d = direction[axis]!;
    if (Math.abs(d) < 1e-12) {
      if (o < -0.5 || o > 0.5) return null;
      continue;
    }
    let near = (-0.5 - o) / d;
    let far = (0.5 - o) / d;
    if (near > far) [near, far] = [far, near];
    entry = Math.max(entry, near);
    exit = Math.min(exit, far);
    if (entry > exit) return null;
  }
  if (exit < 0) return null;
  return (entry >= 0 ? entry : exit) * directionLength;
}

/** Intersects a horizontal table plane, useful for dragging in world coordinates. */
export function intersectRayPlane(ray: Ray, height = 0): Vec3 | null {
  if (Math.abs(ray.direction[1]) < 1e-12) return null;
  const t = (height - ray.origin[1]) / ray.direction[1];
  return t >= 0 ? add3(ray.origin, scale3(ray.direction, t)) : null;
}
